import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { dateTime, fromMinor, money, personName, toMinor } from "@/lib/format";
import { useTill } from "@/lib/till-context";
import type { Item } from "@/lib/types";
import { Button, Field, Input, Modal, Pager, Select, cx, usePaged, useToast } from "./ui";
import { Card, PageHead } from "./Layout";
import { SupplierSelect, UnitSelect, lastPaidOf, useSuppliers } from "./SupplierBits";
import { ItemModal, NameSelect } from "@/pages/Products";

type Shop = { id: string; name: string };
type Line = { itemId: string; qty: string; cost: string; touched?: boolean };
type Draft = { shopId: string; supplierId: string; ref: string; lines: Line[] };
type Past = { id: string; supplierName: string; at: number; ref: string | null; by: string | null; shopId: string; lines: { name: string; unit: string | null; qty: number; cost: number | null }[] };
const KEY = "receive-draft";
const blank: Draft = { shopId: "", supplierId: "", ref: "", lines: [] };

function readDraft(): Draft | null {
  try {
    // Something saved by an older version, or in the wrong shape, is thrown away rather than trusted.
    const d = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Draft> | null;
    if (!d || typeof d !== "object" || !Array.isArray(d.lines)) return null;
    const str = (v: unknown) => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");
    const lines = d.lines.filter((l): l is Line => !!l && typeof l === "object" && typeof (l as Line).itemId === "string").map((l) => ({ itemId: l.itemId, qty: str(l.qty), cost: str(l.cost), touched: !!l.touched }));
    return lines.length ? { shopId: str(d.shopId), supplierId: str(d.supplierId), ref: str(d.ref), lines } : null;
  } catch {
    return null;
  }
}

/** The short form for a product that turns up on a delivery and isn't in the system yet. */
function QuickAdd({ name, shop, onClose, onAdded }: { name: string; shop: Shop; onClose: () => void; onAdded: (it: Item, cost: string, more: boolean) => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name, category: "", brand: "", unit: "", price: "", cost: "" });
  const [busy, setBusy] = useState(false);
  const ready = !!f.name.trim() && toMinor(f.price) > 0;
  const save = async (more: boolean) => {
    setBusy(true);
    try {
      const r = await api<{ item: Item }>(till("/admin/items"), { body: { shopId: shop.id, name: f.name, category: f.category, brand: f.brand, unit: f.unit, price: toMinor(f.price), cost: f.cost === "" ? null : toMinor(f.cost), reorderAt: 0, tracking: "qty", sync: false, active: true, needsDetails: true } });
      onAdded(r.item, f.cost, more);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't add it", "err");
      setBusy(false);
    }
  };
  return (
    <Modal title="Add a new product" sub={`For ${shop.name}. You can add wholesale prices and packs later.`} onClose={onClose} footer={<><Button disabled={busy || !ready} onClick={() => void save(true)}>Add more details</Button><Button tone="primary" disabled={busy || !ready} onClick={() => void save(false)}>Add to delivery</Button></>}>
      <div className="grid gap-3">
        <Field label="Name"><Input value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <NameSelect label="Category" kind="category" shopId={shop.id} value={f.category} onChange={(v) => setF({ ...f, category: v })} />
        <UnitSelect value={f.unit} onChange={(v) => setF({ ...f, unit: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Selling price"><Input inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></Field>
          <Field label="Cost price (optional)"><Input inputMode="decimal" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} /></Field>
        </div>
      </div>
    </Modal>
  );
}

function ChangePrice({ item, onClose, onSaved }: { item: Item; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [v, setV] = useState(fromMinor(item.price));
  const save = async () => {
    try {
      await api(till(`/admin/items/${item.id}`), { method: "PUT", body: { price: toMinor(v) } });
      toast("Selling price changed");
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  return (
    <Modal title="Change selling price" sub={item.name} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button tone="primary" disabled={!toMinor(v)} onClick={() => void save()}>Save</Button></>}>
      <Field label="Selling price"><Input inputMode="decimal" autoFocus value={v} onChange={(e) => setV(e.target.value)} /></Field>
    </Modal>
  );
}

/**
 * Receive a delivery (Buildwise): one shop and one supplier, many products. Costs fill in from what
 * was last paid, a new product can be added on the spot, and an unfinished delivery stays on this device.
 */
export function PlainReceive({ items, shops, preselect, preselectMany, onDone }: { items: Item[]; shops: Shop[]; preselect: string | null; preselectMany: string[]; onDone: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { me } = useTill();
  const canAdd = me?.can ? !!me.can.products : true;
  const sup = useSuppliers();
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const [d, setD] = useState<Draft>(() => readDraft() ?? blank);
  const [mode, setMode] = useState<"list" | "new">(preselect || preselectMany.length ? "new" : "list");
  const [viewing, setViewing] = useState<Past | null>(null);
  const pastQ = useQuery({ queryKey: ["stock-deliveries"], queryFn: () => api<{ deliveries: Past[] }>(till("/admin/stock-deliveries")) });
  const past = pastQ.data?.deliveries ?? [];
  const pastPage = usePaged(past);
  const [pending, setPending] = useState<string[]>(preselectMany);
  const [find, setFind] = useState("");
  const [quick, setQuick] = useState("");
  const [priceOf, setPriceOf] = useState<Item | null>(null);
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState(false);
  const [details, setDetails] = useState<string | null>(null);
  const [queue, setQueue] = useState<string[]>([]);
  const [need, setNeed] = useState<{ units: number; ids: string[] } | null>(null);
  const greeted = useRef(false);
  // A delivery saved on this device can name products, a shop or a supplier that have since been deleted: drop those.
  const cleaned = useRef(false);
  useEffect(() => {
    if (cleaned.current || !items.length || !shops.length || !sup.data) return;
    cleaned.current = true;
    setD((cur) => {
      const lines = cur.lines.filter((l) => byId.has(l.itemId));
      const shopGone = !!cur.shopId && shops.length > 0 && !shops.some((s) => s.id === cur.shopId);
      const supGone = !!cur.supplierId && !!sup.data && !sup.data.suppliers.some((s) => s.id === cur.supplierId);
      if (lines.length === cur.lines.length && !shopGone && !supGone) return cur;
      return { ...cur, lines, shopId: shopGone ? "" : cur.shopId, supplierId: supGone ? "" : cur.supplierId };
    });
  }, [items, shops, sup.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const shopId = d.shopId || (shops.length ? (pending.length ? "" : shops[0]!.id) : "main");
  const shop = shops.find((s) => s.id === shopId) ?? (shops.length ? null : { id: "main", name: "the shop" });

  useEffect(() => {
    if (greeted.current) return;
    greeted.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      if (d.lines.length) localStorage.setItem(KEY, JSON.stringify(d));
      else localStorage.removeItem(KEY);
    } catch {
      /* it still works, it just won't survive leaving the page */
    }
  }, [d]);

  const costFor = (it: Item, supplierId: string) => {
    const c = lastPaidOf(sup.data, it.id, supplierId) ?? it.cost;
    return c == null ? "" : fromMinor(c);
  };
  // A Receive link from Add product or Stock health.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !preselect) return;
    const it = byId.get(preselect);
    if (!it || !sup.data) return;
    started.current = true;
    setD((cur) => {
      const same = cur.shopId === (it.location ?? "");
      const base = same ? cur : { ...blank, shopId: it.location ?? "" };
      const supplierId = base.supplierId || it.mainSupplierId || "";
      if (base.lines.some((l) => l.itemId === it.id)) return { ...base, supplierId };
      return { ...base, supplierId, lines: [...base.lines, { itemId: it.id, qty: "", cost: costFor(it, supplierId) }] };
    });
  }, [preselect, byId, sup.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setShop = (id: string) => {
    const kept = d.lines.filter((l) => byId.get(l.itemId)?.location === id);
    if (kept.length < d.lines.length) toast("Products from the other shop came off this delivery", "info");
    const fromPending = items.filter((i) => pending.includes(i.id) && i.location === id);
    if (fromPending.length) setPending([]);
    setD({ ...d, shopId: id, lines: [...kept, ...fromPending.filter((i) => !kept.some((l) => l.itemId === i.id)).map((i) => ({ itemId: i.id, qty: "", cost: costFor(i, d.supplierId) }))] });
  };
  const setSupplier = (id: string) => {
    setBad(false);
    setD({ ...d, supplierId: id, lines: d.lines.map((l) => { if (l.touched) return l; const it = byId.get(l.itemId); return it ? { ...l, cost: costFor(it, id) } : l; }) });
  };
  const setLine = (i: number, patch: Partial<Line>) => setD({ ...d, lines: d.lines.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const addLine = (it: Item, cost?: string) => {
    setFind("");
    setD((cur) => (cur.lines.some((l) => l.itemId === it.id) ? cur : { ...cur, lines: [...cur.lines, { itemId: it.id, qty: "", cost: cost ?? costFor(it, cur.supplierId) }] }));
  };

  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hits = words.length ? items.filter((i) => i.active && i.tracking === "qty" && i.location === shopId && !d.lines.some((l) => l.itemId === i.id) && words.every((w) => i.name.toLowerCase().includes(w))).slice(0, 8) : [];
  const exact = items.some((i) => i.location === shopId && i.name.toLowerCase() === find.trim().toLowerCase());
  const units = d.lines.reduce((n, l) => n + (Number(l.qty) || 0), 0);
  const total = d.lines.reduce((n, l) => n + toMinor(l.cost) * (Number(l.qty) || 0), 0);

  const submit = async () => {
    if (!d.supplierId) return setBad(true);
    setBusy(true);
    try {
      const lines = d.lines.filter((l) => Number(l.qty) > 0);
      const r = await api<{ units: number }>(till("/admin/receive"), { body: { supplierId: d.supplierId, ref: d.ref, lines: lines.map((l) => ({ itemId: l.itemId, qty: Number(l.qty), cost: l.cost === "" ? null : toMinor(l.cost) })) } });
      const unfinished = lines.map((l) => byId.get(l.itemId)).filter((i) => i?.needsDetails).map((i) => i!.id);
      toast(`${r.units.toLocaleString("en-GB")} added to shop stock`);
      setD({ ...blank, shopId: d.shopId });
      setMode("list");
      void qc.invalidateQueries({ queryKey: ["stock-deliveries"] });
      if (unfinished.length) setNeed({ units: r.units, ids: unfinished });
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
      onDone();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save the delivery", "err");
    } finally {
      setBusy(false);
    }
  };
  const nextDetail = () => {
    const [first, ...rest] = queue;
    setQueue(rest);
    setDetails(first ?? null);
  };

  const after = (
    <>
      {need ? (
        <Modal title={`${need.units.toLocaleString("en-GB")} added to shop stock`} sub={`${need.ids.length} new product${need.ids.length === 1 ? " needs" : "s need"} details`} onClose={() => setNeed(null)} footer={<><Button onClick={() => setNeed(null)}>Later</Button><Button tone="primary" onClick={() => { const [first, ...rest] = need.ids; setQueue(rest); setDetails(first!); setNeed(null); }}>Add details now</Button></>}>
          {need.ids.map((id) => <p key={id} className="border-b border-slate-100 py-2 text-sm font-semibold">{byId.get(id)?.name}</p>)}
          <p className="mt-2 text-xs text-slate-500">Wholesale prices and packs may be missing. Until then it sells at its normal price.</p>
        </Modal>
      ) : null}
      {details ? <ItemModal id={details} shops={shops} shopId={shopId} onClose={nextDetail} onSaved={onDone} /> : null}
    </>
  );
  const shopOf = (id: string) => shops.find((s) => s.id === id)?.name;
  if (mode === "list") {
    return (
      <>
        <PageHead title="Receive stock"><Button tone="primary" className="max-md:w-full" onClick={() => setMode("new")}>+ New delivery</Button></PageHead>
        {d.lines.length ? <button type="button" onClick={() => setMode("new")} className="mb-3 flex w-full items-center justify-between gap-3 rounded-2xl bg-brand-tint px-4 py-3 text-left text-brand-ink"><span><b className="block text-[15px]">Finish your delivery</b><span className="text-[13px]">{d.lines.length} product{d.lines.length === 1 ? "" : "s"} waiting to be saved</span></span><span className="text-xl">›</span></button> : null}
        <Card>
          {pastPage.rows.map((x) => (
            <button key={x.id} type="button" onClick={() => setViewing(x)} className="flex min-h-[68px] w-full items-center justify-between gap-3 !rounded-none border-b border-slate-100 px-4 py-2.5 text-left last:border-b-0 hover:bg-slate-50">
              <span className="min-w-0"><b className="block text-[15px] font-semibold">{x.supplierName}</b><span className="text-[13px] text-slate-500">{[dateTime(x.at), shopOf(x.shopId), `${x.lines.length} product${x.lines.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}</span></span>
              <b className="flex-none">{money(x.lines.reduce((n, l) => n + (l.cost ?? 0) * l.qty, 0))}</b>
            </button>
          ))}
          {pastQ.data && !past.length ? <p className="px-4 py-10 text-center text-sm text-slate-500">No deliveries yet. Tap New delivery when stock arrives.</p> : null}
          <Pager page={pastPage.page} size={pastPage.size} total={pastPage.total} onPage={pastPage.setPage} />
        </Card>
        {viewing ? (
          <Modal wide title={viewing.supplierName} sub={[dateTime(viewing.at), shopOf(viewing.shopId), viewing.ref, viewing.by ? personName(viewing.by) : null].filter(Boolean).join(" · ")} onClose={() => setViewing(null)}>
            {viewing.lines.map((l, i) => <div key={i} className="flex justify-between gap-3 border-b border-slate-100 py-2.5 text-sm"><span className="min-w-0"><b className="block font-semibold">{l.name}</b><span className="text-xs text-slate-500">{l.qty.toLocaleString("en-GB")} {l.unit ?? "piece"}{l.qty === 1 ? "" : "s"}{l.cost != null ? ` at ${money(l.cost)}` : ""}</span></span><b className="flex-none">{l.cost == null ? "-" : money(l.cost * l.qty)}</b></div>)}
          </Modal>
        ) : null}
        {after}
      </>
    );
  }
  return (
    <>
    <PageHead title="New delivery"><Button onClick={() => setMode("list")}>Back to deliveries</Button></PageHead>
    <Card>
      <div className="flex flex-col gap-4 p-4 sm:p-5">
        <div className="grid gap-3 md:grid-cols-3">
          {shops.length ? (
            <Field label="Which shop is this delivery for?">
              <Select value={shopId} onChange={(e) => setShop(e.target.value)}>
                <option value="">Choose a shop</option>
                {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
          ) : null}
          <div>
            <SupplierSelect label="Supplier" value={d.supplierId} blank="Choose a supplier" onChange={setSupplier} />
            {bad ? <span className="mt-1 block text-[13px] font-semibold text-stop">Choose the supplier</span> : null}
          </div>
          <Field label="Invoice or waybill number (optional)"><Input value={d.ref} maxLength={60} onChange={(e) => setD({ ...d, ref: e.target.value })} /></Field>
        </div>
        {shop ? (
          <div className="relative">
            <Field label="Add a product"><Input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Type a product name" autoComplete="off" onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) { e.preventDefault(); addLine(hits[0]); } }} /></Field>
            {find.trim() ? (
              <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl bg-white p-1 shadow-[0_12px_30px_rgba(11,21,23,0.18)] ring-1 ring-slate-200">
                {hits.map((i) => (
                  <button key={i.id} type="button" onClick={() => addLine(i)} className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-slate-50">
                    <span className="min-w-0 font-semibold">{i.name}</span>
                    <span className="flex-none text-xs text-slate-500">{(i.onHand ?? 0).toLocaleString("en-GB")} in stock</span>
                  </button>
                ))}
                {!exact ? (canAdd ? (
                  <button type="button" onClick={() => setQuick(find.trim())} className="flex min-h-11 w-full items-center px-3 py-1.5 text-left text-sm font-bold text-brand hover:bg-brand-tint">+ Add "{find.trim()}" as a new product</button>
                ) : <p className="px-3 py-3 text-sm text-slate-500">{hits.length ? "" : "This product isn't in the system. Ask a manager to add it. This delivery stays saved on this device."}</p>) : null}
              </div>
            ) : null}
          </div>
        ) : <p className="rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">Choose the shop first.</p>}
        {pending.length && !shopId ? <p className="text-[13px] font-semibold text-warn-ink">Your new product is in {pending.length} shops. Choose which shop received this delivery.</p> : null}
        {d.lines.map((l, i) => {
          const it = byId.get(l.itemId);
          if (!it) return null;
          const q = Number(l.qty) || 0;
          const c = l.cost === "" ? null : toMinor(l.cost);
          const had = Math.max(0, it.onHand ?? 0);
          const avg = c != null && it.cost != null && q > 0 && c !== it.cost ? Math.round((it.cost * had + c * q) / (had + q)) : null;
          const paid = lastPaidOf(sup.data, it.id, d.supplierId);
          return (
            <div key={l.itemId} className="rounded-xl bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0"><b className="block text-sm font-semibold">{it.name}</b><span className="text-xs text-slate-500">{had.toLocaleString("en-GB")} in stock now{q ? `, ${(had + q).toLocaleString("en-GB")} after this` : ""}{paid != null ? ` · Last paid this supplier ${money(paid)}` : ""}</span></span>
                <button type="button" aria-label={`Remove ${it.name}`} onClick={() => setD({ ...d, lines: d.lines.filter((_, j) => j !== i) })} className="h-11 w-11 flex-none text-xl text-slate-500 hover:text-red-700">×</button>
              </div>
              <div className="mt-2 grid grid-cols-2 items-end gap-2.5 md:grid-cols-[140px_160px_1fr]">
                <Field label="Quantity"><Input inputMode="numeric" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value.replace(/\D/g, "") })} placeholder="0" /></Field>
                <Field label="Cost each"><Input inputMode="decimal" value={l.cost} onChange={(e) => setLine(i, { cost: e.target.value, touched: true })} placeholder="0.00" /></Field>
                <span className="col-span-2 flex h-11 items-center justify-between gap-2 text-sm md:col-span-1 md:justify-end"><span className="text-slate-500">Line cost</span><b>{money((c ?? 0) * q)}</b></span>
              </div>
              {avg != null ? (
                <p className="mt-2 rounded-lg bg-brand-tint px-3 py-2 text-[13px] font-semibold text-brand-ink">
                  Average cost goes from {money(it.cost!)} to {money(avg)}. You sell at {money(it.price)}, so profit is now {(((it.price - avg) / it.price) * 100).toFixed(1)}%.{" "}
                  {canAdd ? <button type="button" className="font-bold underline" onClick={() => setPriceOf(it)}>Change selling price</button> : null}
                </p>
              ) : null}
            </div>
          );
        })}
        {shop && !d.lines.length ? <p className="rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">Search for a product above to start this delivery.</p> : null}
        <div className="sticky bottom-[88px] z-10 -mx-1 flex flex-wrap items-center justify-end gap-3.5 rounded-2xl bg-white p-3 shadow-[0_-6px_24px_rgba(11,21,23,0.1)] ring-1 ring-slate-200 md:bottom-4">
          {units ? <span className="text-sm text-slate-500">{units.toLocaleString("en-GB")} unit{units === 1 ? "" : "s"} · cost <b className="text-ink">{money(total)}</b></span> : null}
          {d.lines.length ? <Button onClick={() => setD({ ...blank, shopId: d.shopId })}>Clear</Button> : null}
          <Button tone="primary" onClick={() => void submit()} disabled={busy || !units} className={cx("w-full sm:w-auto")}>
            {busy ? "Saving…" : units ? `Add ${units.toLocaleString("en-GB")} to shop stock` : "Add to shop stock"}
          </Button>
        </div>
      </div>
      {quick && shop ? (
        <QuickAdd name={quick} shop={shop} onClose={() => setQuick("")} onAdded={(it, cost, more) => {
          setQuick("");
          addLine(it, cost);
          onDone();
          toast(`${it.name} added to this delivery`);
          if (more) setDetails(it.id);
        }} />
      ) : null}
      {priceOf ? <ChangePrice item={priceOf} onClose={() => setPriceOf(null)} onSaved={onDone} /> : null}
      {after}
    </Card>
    </>
  );
}
